import { createContext, useContext } from 'react';
import type { Observable } from 'rxjs';
import type { RepresentativeOption, SecurityTypes } from '../types';
import type {
  OTCFixedRateLoanProductTemplate,
  OTCFloatingRateLoanProductTemplate,
  OTCForwardProductTemplate,
  OTCOptionProductTemplate,
  OTCRepoProductTemplate,
} from '../types/OTC/otcSecurityServerTypes';
import type { Security } from '../types/Security';

export const SecuritiesContext = createContext<SecuritiesContextProps | undefined>(undefined);
SecuritiesContext.displayName = 'SecuritiesContext';

export function useSecuritiesContext() {
  const context = useContext(SecuritiesContext);
  if (context === undefined) {
    throw new Error('Missing SecuritiesContext.Provider further up in the tree. Did you forget to add it?');
  }
  return context;
}

export interface SecuritiesContextProps {
  isLoaded: boolean;
  hasBPSPricingSecurities: boolean;
  securitiesBySymbolObs: Observable<Map<string, Security>>;
  securitiesBySymbol: Map<string, Security>;
  securitiesList: Security[];
  /**
   * A sorted list of securities based on their `Rank` value.
   */
  securitiesListSortedByRank: Security[];
  /** The searchable securities is a sorted list of all securities by DisplayName, with any recent selections weighted to be sorted to the top as well.
   * Note that this list has securities with EndTime (indicates it was soft deleted) and all expired securities filtered out.
   * This data set is used for the SymbolSelectors in the app, for example.
   */
  searchableSecurities: Security[];
  perps: SecurityTypes['Perp'][];
  perpsByCurrencyPair: Map<string, SecurityTypes['Perp'][]>;
  futures: SecurityTypes['Future'][];
  futuresByCurrencyPair: Map<string, SecurityTypes['Perp'][]>;
  futuresByMarketByCurrency: Map<string, Map<string, Set<SecurityTypes['Future']>>>;

  options: SecurityTypes['Option'][];
  expirationByMarketByCurrencyIdentity: Map<string, Map<string, Set<string>>>;
  optionSecurityBySymbol: Map<string, SecurityTypes['Option']>;
  optionSecurityBySymbolObs: Observable<Map<string, SecurityTypes['Option']>>;
  /** Given a compound key of BaseCurrency, UnderlyingCode, and Markets gives a Security which is representative
   * of the basket of Securities with that combination. This is to allow callers to look up properties
   * like SettlementCurrency which will be the same for all strike prices and expirations given the compound key
   */
  representativeOptionSecurityByKey: Map<string, RepresentativeOption>;
  strikesByOptionKey: Map<string, string[]>;
  /** A mapping from Currency to all spot trading pairs where the indexed currency is the Base Currency of the trading pair. */
  spotTradingPairsByBaseCurrency: Map<string, SecurityTypes['Spot'][]>;
  /** A mapping from Currency to all spot trading pairs where the indexed currency is the Quote Currency of the trading pair. */
  spotTradingPairsByQuoteCurrency: Map<string, SecurityTypes['Spot'][]>;

  otcFixedRateLoansByDisplaySymbol: Map<string, OTCFixedRateLoanProductTemplate>;
  otcForwardsByDisplaySymbol: Map<string, OTCForwardProductTemplate>;
  otcOptionsByDisplaySymbol: Map<string, OTCOptionProductTemplate>;
  otcFloatingRateLoansByDisplaySymbol: Map<string, OTCFloatingRateLoanProductTemplate>;
  otcReposByDisplaySymbol: Map<string, OTCRepoProductTemplate>;
}
